import { useState } from "react";
import { PageHeader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sparkle, EnvelopeSimple, NotePencil, Users } from "@phosphor-icons/react";

export default function AIAssistant() {
  return (
    <div data-testid="ai-page">
      <PageHeader
        eyebrow="Powered by Claude Sonnet 4.5"
        title="AI Assistant"
        description="Draft emails, summarize call notes, and match interpreters to projects."
      />
      <Tabs defaultValue="email">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="email" data-testid="tab-email"><EnvelopeSimple size={14} className="mr-1.5" /> Draft email</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary"><NotePencil size={14} className="mr-1.5" /> Summarize notes</TabsTrigger>
          <TabsTrigger value="match" data-testid="tab-match"><Users size={14} className="mr-1.5" /> Match interpreters</TabsTrigger>
        </TabsList>
        <TabsContent value="email"><AITool endpoint="/ai/draft-email" label="Describe what you want to send" placeholder="e.g., Confirm interpreter for USCIS interview July 12 in Boston, ask client to sign contract." testid="ai-email" /></TabsContent>
        <TabsContent value="summary"><AITool endpoint="/ai/summarize-notes" label="Paste raw call/meeting notes" placeholder="Paste your call transcript or bullet notes here…" testid="ai-summary" /></TabsContent>
        <TabsContent value="match"><AITool endpoint="/ai/match-interpreters" label="Describe project requirements" placeholder="French↔English, medical, in-person Boston, 2 hours, ideally $65/hr…" testid="ai-match" /></TabsContent>
      </Tabs>
    </div>
  );
}

function AITool({ endpoint, label, placeholder, testid }) {
  const [prompt, setPrompt] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!prompt.trim()) return toast.error("Enter a prompt");
    setBusy(true);
    try {
      const r = await api.post(endpoint, { prompt });
      setOut(r.data.text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid={testid}>
      <div className="card-flat p-5">
        <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
        <Textarea rows={12} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={placeholder} className="mt-2" data-testid={`${testid}-input`} />
        <Button onClick={run} disabled={busy} className="mt-4 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid={`${testid}-run`}>
          <Sparkle size={14} weight="fill" className="mr-1.5" /> {busy ? "Working…" : "Generate"}
        </Button>
      </div>
      <div className="card-flat p-5">
        <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Result</Label>
        <div className="mt-2 min-h-[240px] whitespace-pre-wrap text-sm text-slate-800 font-mono bg-slate-50 border border-slate-200 rounded p-3" data-testid={`${testid}-output`}>
          {out || <span className="text-slate-400">Response will appear here.</span>}
        </div>
      </div>
    </div>
  );
}
